import { Skeleton } from '@heroui/react';

import { DeploymentDataItem } from '../../hooks/useDeploymentsData';

import i18n from '~/common/helpers/i18n';
import {
  DeployedByCell,
  DeployedByCellSkeleton,
  DeploymentActionsCell,
  DeploymentActionsCellSkeleton,
  DeployStatusCell,
  DeployStatusCellSkeleton,
  PipelineInfosCell,
  PipelineInfosCellSkeleton
} from '~/components/Table/cells';
import { RenderCellProps } from '~/components/Table/Table';

// naming following the pattern for the old table handlers. Change when we kill the old table
// TODO: Replace 'any' types with proper TypeScript types when refactoring handlers
export type ActionHandlers = {
  onClick: (deployment: any) => void;
  goToPipeline: (id: string) => void;
  redeploy: (deployment: any) => void;
  openDetailPromote: (deployment: any) => void;
  openModalRollbackDeploy: (deployment: any) => void;
  onDeleteDeploy: (deployment: any) => void;
  onViewExecutions: (pipelineName: string) => void;
  onOpenVersionDiff: (pipelineName: string, version: string) => void;
  environment: string;
};

export const renderCell = ({
  item,
  columnKey,
  handlers,
  isLoading
}: RenderCellProps<DeploymentDataItem, ActionHandlers>) => {
  if (isLoading) {
    switch (columnKey) {
      case 'pipelineName':
        return <PipelineInfosCellSkeleton />;
      case 'deployStatus':
        return <DeployStatusCellSkeleton />;
      case 'deployedBy':
        return <DeployedByCellSkeleton />;
      case 'deploymentActions':
        return <DeploymentActionsCellSkeleton />;
      default:
        return <Skeleton className='h-4 w-20 rounded' />;
    }
  }

  switch (columnKey) {
    case 'pipelineName':
      return (
        <PipelineInfosCell
          name={item.pipelineData.name}
          version={item.pipelineData.version}
          description={item.pipelineData.description}
          trigger={{
            category: item.pipelineData.triggerCategory,
            triggerSpec: item.pipelineData.triggerSpec,
            iconURL: item.pipelineData.trigger?.iconURL,
            // BACKEND-API-TRIGGER: drop isUsingApiTrigger + flowSpec from trigger payload.
            hasApiTrigger: item.pipelineData.hasApiTrigger,
            isUsingApiTrigger: item.pipelineData.isUsingApiTrigger,
            flowSpec: item.pipelineData.flowSpec
          }}
        />
      );
    case 'deployStatus':
      return (
        <DeployStatusCell
          status={item.statusData.status}
          environment={item.statusData.environment}
          deployment={item.deployment}
        />
      );
    case 'deployedBy':
      return (
        <DeployedByCell
          deployedBy={item.deployedByData.deployedBy}
          deploymentTime={item.deployedByData.deploymentTime}
          engineVersion={item.deployedByData.engineVersion}
        />
      );
    case 'deploymentActions': {
      if (!handlers) {
        return null;
      }

      const isRunMovePipelineShow = handlers.environment !== 'prod';

      const newEnvironment = handlers.environment === 'test' ? 'prod' : 'test';

      const promoteValue = i18n.t(`action.move_to_${newEnvironment}`);

      return (
        <DeploymentActionsCell
          onGoToPipeline={() => handlers.goToPipeline(item.pipelineData.id)}
          onRedeploy={
            () =>
              setTimeout(
                () =>
                  handlers.redeploy({
                    ...(item.deployment?.pipeline ?? {}),
                    isPipelineEngine:
                      item.deployment?.deploymentStatus?.pipelineEngineBeta ===
                      'true'
                  }),
                200
              ) // There is something happening with the redeploy that's not working without the timeout when inside the heroui dropdown
          }
          onPromote={() =>
            setTimeout(
              () =>
                handlers.openDetailPromote({
                  text: promoteValue,
                  newEnvironment,
                  ...item.deployment
                }),
              200
            )
          }
          onRollback={() => handlers.openModalRollbackDeploy(item.deployment)}
          onDeleteDeploy={() => handlers.onDeleteDeploy(item.deployment)}
          onViewExecutions={() =>
            handlers.onViewExecutions(item.deployment.pipeline?.name as string)
          }
          onOpenVersionDiff={() =>
            handlers.onOpenVersionDiff(
              item.deployment.pipeline?.name ?? '',
              item.deployment.pipeline
                ? `${item.deployment.pipeline.versionMajor}.${item.deployment.pipeline.versionMinor}`
                : ''
            )
          }
          isDisabled={
            item.statusData.status === 'starting' ||
            item.statusData.status === 'degraded' ||
            item.statusData.status === 'deleting' ||
            item.statusData.status === 'service_error'
          }
          showPromote={isRunMovePipelineShow}
          environment={handlers.environment}
          pipelineVersionMajor={item.deployment.pipeline?.versionMajor}
          pipelineVersionMinor={item.deployment.pipeline?.versionMinor}
        />
      );
    }
    default:
      return null;
  }
};
